import { config } from '../config.ts';
import { feedbackText, hintText, sayKey, type FeedbackLang } from '../lib/feedbackVoice.ts';
import { lineIn } from '../lib/teacher.ts';
import type { BanterLine } from '../types.ts';
import { audioIds } from '../speech/audioIds.ts';
import { pickBanter } from '../lib/banter.ts';
import { typedAttempt } from '../lib/coach.ts';
import { checkTyped } from '../lib/typed.ts';
import type { ClozeTask } from '../types.ts';
import type { TaskEnv, TaskView } from './ctx.ts';
import { button, h, shake } from './dom.ts';
import { tx, txAttr, txText } from './i18n.ts';
import type { Pair } from './i18n.ts';
import { dialogueLine } from './dialogue.ts';
import { fitProps, fitToWindow, icon, translateButton } from './kit.ts';
import { L } from './taskText.ts';

const T = {
  missingWord: { en: 'Missing word', pl: 'Brakujące słowo' },
  review: { en: 'Review', pl: 'Powtórka' },
  fillIn: { en: 'Fill in the blank', pl: 'Uzupełnij wyrażenie' },
  fillWord: { en: 'Fill in the missing word.', pl: 'Uzupełnij brakujące słowo.' },
  tapHint: { en: 'Tap the lightbulb for a hint.', pl: 'Dotknij żarówki, żeby dostać podpowiedź.' },
} as const satisfies Record<string, Pair>;

export function renderCloze(task: ClozeTask, env: TaskEnv): TaskView {
  const { word, sentence } = task;
  // Kurs v2: dziecko wpisuje formę słowa, jaka stoi w zdaniu („apples”), a oceniamy ją tak samo jak samo słówko; reszta (SRS, wynik) dotyczy celu.
  const gap = task.answer ?? word.word;
  const { ctx } = env;
  const { player, banter, teacher } = ctx;
  const comp = env.companion;
  let attempts = 0;
  let hinted = false;
  let finished = false;
  let note: 'type' | 'hint' | 'none' | null = null; // co jest napisane pod zdaniem (podpowiedź albo prośba o wpisanie słowa) — przerysowujemy po zmianie języka
  const renderNote = () => {
    const lang = langNow();
    // podpis pod poleceniem w karcie z żarówką: podpowiedź (litery), prośba o wpisanie słowa, a na starcie zachęta do dotknięcia żarówki; po rozwiązaniu pusto (wynik pokazuje kolor pola)
    feedback.textContent = finished || note === 'none' ? '' : note === 'hint' ? hintText(gap[0] ?? '', gap.length, lang) : note === 'type' ? feedbackText('typeWord', lang) : T.tapHint[lang];
  };

  // dzielimy zdanie na część przed i po słówku, żeby wstawić pole w miejsce luki
  const re = new RegExp(`\\b${gap.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
  const m = re.exec(sentence);
  const before = m ? sentence.slice(0, m.index) : sentence + ' ';
  const after = m ? sentence.slice(m.index + m[0].length) : '';
  const tail = /^[^\s]*/.exec(after)?.[0] ?? ''; // znak tuż za luką („.”, „?”) zostaje przy polu — nie zawija się do osobnej linijki

  const input = txAttr(ctx, h('input', {
    type: 'text',
    class: 'blank',
    'data-testid': 'cloze-input',
    autocapitalize: 'off',
    autocomplete: 'off',
    autocorrect: 'off',
    spellcheck: 'false',
    enterkeyhint: 'done',
    style: `width: calc(${Math.max(4, gap.length)}ch + 0.8em)`,
  }), 'aria-label', T.missingWord);
  const feedback = h('p', { class: 'feedback', 'data-testid': 'feedback', 'aria-live': 'polite' });
  const actions = h('div', { class: 'actions-bar single end' });
  // język uwag bota (przełącznik EN/PL u góry): napisy i głos zawsze w jednym, wybranym języku
  const langNow = (): FeedbackLang => teacher?.lang() ?? config.teacher.defaultLang;
  const sentenceId = task.audioId ?? audioIds.sentence(task.word.sentenceId);
  const playSentence = () => player.play(sentenceId, { text: sentence, lang: 'en' });
  const slowId = `${sentenceId}.slow`;
  /** Linijka z puli bota w wybranym języku (nagranie b.<id> albo b.<id>.pl; bez pliku głos zapasowy z tym samym tekstem). */
  const lineClip = (line: BanterLine) => {
    const c = lineIn(line, langNow());
    return { id: c.id, fallback: { text: c.text, lang: c.lang } };
  };

  /**
   * Bot w zadaniach: po błędnej próbie (i po poprawnej, gdy było potknięcie) dopowiada krótką reakcję w pasku; wynik nadal ocenia aplikacja.
   * `fallback` — gotowa uwaga z nagrania, gdy bot nic nie mówi (warstwa wyłączona albo odpowiedź zawiodła).
   */
  const reactTo = (passed: boolean, typed: string, after?: Promise<unknown>, fallback?: () => void) => {
    const facts = typedAttempt({ no: attempts, passed, typed: typed.trim() });
    if (comp?.wants(facts)) {
      void comp.react(facts, after ? { after } : {}).then((out) => {
        if (out === 'failed' || out === 'skipped') fallback?.();
      });
    } else fallback?.();
  };

  // `correct` = poprawnie za pierwszym razem i bez podpowiedzi (liczy się do powtórek); `solved` = dziecko w końcu wpisało dobre słowo
  const finish = (correct: boolean, reveal: boolean, typed = '', solved = correct) => {
    finished = true;
    input.disabled = true;
    if (reveal) input.value = gap;
    input.classList.remove('right', 'wrong');
    input.classList.add(solved ? 'right' : 'wrong'); // zielone także po podpowiedzi albo drugiej próbie (liczy się to, że dziecko samo wpisało słowo)
    note = 'none';
    feedback.textContent = ''; // wynik pokazuje kolor luki (zielona / czerwona z odkrytym słowem); „Dobrze!” mówi bot głosem ElevenLabs, napisu nie ma
    comp?.quiet(); // reakcja bota przychodzi po zdaniu: najpierw nagranie
    const played = solved
      ? player.playAll([sayKey('good', langNow()), { id: sentenceId, fallback: { text: sentence, lang: 'en' } }], config.teacher.gapMs)
      : playSentence();
    reactTo(solved, typed, played);
    hintCard.disabled = true;
    actions.className = 'actions-bar single end';
    // odsłuch zdania jest w dymku (🔊 i 🐢), więc na dole zostaje sam „Dalej”
    actions.replaceChildren(button([tx(ctx, L.next), icon('arrow')], () => env.done({ taskId: task.id, kind: 'cloze', ref: word.word, correct, attempts, review: task.review }), { class: 'btn primary', testid: 'next' }));
  };

  const check = () => {
    if (finished) return;
    const typed = input.value;
    if (typed.trim() === '') {
      note = 'type';
      renderNote();
      return;
    }
    attempts++;
    const verdict = checkTyped(gap, typed);
    if (verdict === 'correct') return finish(attempts === 1 && !hinted, false, typed, true);
    shake(input);
    input.classList.add('wrong'); // czerwone do następnej zmiany w polu
    // po kilku nieudanych próbach obok „Sprawdź” pojawia się „Popraw” (odkrywa słowo); nic nie rozwiązuje się samo
    const offer = attempts >= config.session.fixAfterAttempts;
    if (offer) offerFix();
    note = 'none';
    feedback.textContent = ''; // uwagę (np. „Prawie! Sprawdź literki.”) mówi bot głosem ElevenLabs, napisu nie ma — wynik pokazuje kolor pola
    const clips = offer
      ? [sayKey('notYetFix', langNow())]
      : verdict === 'almost'
        ? [sayKey('almostLetters', langNow())]
        : [...(banter.length > 0 ? [lineClip(pickBanter(banter, 'hint', attempts))] : []), sayKey('tryAgain', langNow())];
    reactTo(false, typed, undefined, () => void player.playAll(clips, config.teacher.gapMs));
    input.focus();
  };

  const fixBtn = txAttr(ctx, button(tx(ctx, L.showAnswer), () => {
    if (finished) return;
    finish(false, true, input.value);
  }, { class: 'btn ghost', testid: 'fix' }), 'title', L.showAnswerTitle);
  let fixOffered = false;
  const offerFix = () => {
    if (fixOffered || finished) return;
    fixOffered = true;
    actions.className = 'actions-bar two';
    actions.replaceChildren(fixBtn, checkBtn);
  };

  const hint = () => {
    hinted = true;
    note = 'hint';
    renderNote();
    input.focus();
  };

  input.addEventListener('input', () => input.classList.remove('wrong'));
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') check();
  });
  const checkBtn = button(tx(ctx, L.check), check, { class: 'btn primary', testid: 'check' });
  actions.append(checkBtn);

  // Rozmowa (patch-50): jak w scence — wiersz z głową i imieniem mówcy; nad nim, gdy jest, pytanie rozmówcy (kontekst), pod nim zdanie z polem na brakujące słowo.
  // Każdy wiersz ma własne 🔊 (normalnie) i 🐢 (wolno), więc czerwonego głośnika nad zdaniem już nie ma; maskotki też nie ma.
  const cue = task.cue;
  const cuePl = cue ? h('p', { class: 'dlg-pl pl cue-pl', 'data-testid': 'cue-pl' }, cue.pl) : null;
  const cueRow = cue
    ? dialogueLine(ctx, {
        who: cue.who,
        body: cue.text,
        pl: cuePl!,
        testid: 'cloze-cue',
        textTestid: 'cue-text',
        play: { testid: 'cue-play', label: L.listenToSentence, run: () => { comp?.quiet(); return player.play(cue.audioId, { text: cue.text, lang: 'en' }); } },
        slow: { testid: 'cue-slow', label: L.slowly, run: () => { comp?.quiet(); return player.play(cue.slowAudioId ?? cue.audioId, { text: cue.text, lang: 'en', slow: true }); } },
      })
    : null;

  const prompt = h('p', { class: 'dlg-pl pl', 'data-testid': 'prompt' }, task.pl);
  const mineRow = dialogueLine(ctx, {
    ...(task.who ? { who: task.who } : {}),
    ...(task.me ? { me: true } : {}),
    body: h('p', { class: 'sentence dlg-sentence' }, before, h('span', { class: 'blank-end' }, h('span', { class: 'blank-wrap' }, input), tail), after.slice(tail.length)),
    pl: prompt,
    testid: 'cloze-line',
    play: { testid: 'cloze-listen', label: L.listenToSentence, run: () => { comp?.quiet(); return playSentence(); } },
    slow: { testid: 'cloze-listen-slow', label: L.slowly, run: () => { comp?.quiet(); return player.play(slowId, { text: sentence, lang: 'en', slow: true }); } },
  });
  // ikona 文A: pokazuje i chowa polskie zdanie (domyślnie widać je — to wskazówka do zadania, jak na makiecie)
  const toggle = translateButton(true, (on) => {
    prompt.hidden = !on;
    if (cuePl) cuePl.hidden = !on;
  }, ctx);

  // Karta z żarówką pod rozmową (makieta „Missing words”): polecenie i podpis — na starcie zachęta, po dotknięciu żarówki podpowiedź (litery), przy pustym polu prośba o wpisanie słowa.
  // To też przycisk podpowiedzi (zastąpił przycisk „Podpowiedź” w dolnym pasku, na dole zostaje „Sprawdź”).
  const hintCard = h('button', { type: 'button', class: 'hint-card', 'data-testid': 'hint', onclick: hint },
    h('span', { class: 'hint-bulb', 'aria-hidden': 'true' }, icon('bulb')),
    h('span', { class: 'hint-copy' }, txText(ctx, h('span', { class: 'hint-title' }), T.fillWord), feedback),
  );
  txAttr(ctx, hintCard, 'title', L.hint);
  renderNote();

  const el = h(
    'section',
    { class: 'task cloze exercise screen', 'data-task': 'cloze', 'data-testid': 'task-cloze', 'data-review': task.review ? '1' : '0', ...fitProps(Math.max(task.sentence.length, task.cue?.text.length ?? 0)) },
    h('div', { class: 'tools-row' }, txText(ctx, h('p', { class: 'chip' }), task.review ? T.review : T.fillIn), toggle),
    h('div', { class: 'ex-main screen-main' },
      h('div', { class: 'dlg', 'data-testid': 'dialogue' }, cueRow?.el, mineRow.el),
      hintCard,
    ),
    actions,
  );
  queueMicrotask(() => input.focus());
  const offLang = teacher?.onChange(() => renderNote());
  const fit = fitToWindow(el, { grow: true });
  return { el, destroy: () => { fit.dispose(); offLang?.(); } };
}
